import type { PartialMessage } from "@bufbuild/protobuf";
import {
  CampaignLottery_Result,
  type UserRepresentation,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  type ApplyCampaignRequest,
  ApplyCampaignResponse,
  type ApplyChargeBonusCampaignRequest,
  ApplyChargeBonusCampaignResponse,
  type ApplyRepresentativeSignupCampaignRequest,
  ApplyRepresentativeSignupCampaignResponse,
  ApplySelfRepresentativeSignupCampaignResponse,
  type CreateMyInviterRequest,
  CreateMyInviterResponse,
  type GetMyCampaignStatusesRequest,
  GetMyCampaignStatusesResponse,
  type GetMyCampaignStatusesResponse_ChargeBonusCampaignStatus,
  GetMyInvitationResponse,
  GetRepresentationsResponse,
} from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import type { OsakiChargeBonusCampaignTerm } from "../../config";
import { getCurrentOsakiChargeBonusCampaignPeriod } from "../../features/charge/osaki-charge-bonus-campaign";
import { getTenantSlug } from "../utils";

const osakiChargeBonus1stFundSource = {
  id: "e0d4d184-8733-48b8-99be-bd363c0dffb5",
  name: "おおさきポイントアップ商品券",
  forbidTransfer: false,
};

const osakiChargeBonus2ndFundSource = {
  id: "59088b62-8822-40f5-9177-5800a5f61912",
  name: "おおさきポイントアップ商品券",
  forbidTransfer: false,
};

const osakiChargeBonus3rdFundSource = {
  id: "b98cc028-47b3-4de7-9172-91467f562e29",
  name: "おおさきポイントアップ商品券",
  forbidTransfer: false,
};

type OsakiChargeBonusMockState =
  | "not-applied"
  | "applied"
  | "winner"
  | "consumed";

type OsakiChargeBonusMockStatus = {
  state: OsakiChargeBonusMockState;
  maxQuantity: bigint;
  consumedQuantity: bigint;
};

const defaultOsakiChargeBonusMockStatus = (): OsakiChargeBonusMockStatus => ({
  state: "not-applied",
  maxQuantity: BigInt(2),
  consumedQuantity: BigInt(0),
});

const createOsakiChargeBonusMockStatus = (
  envValue: unknown,
): OsakiChargeBonusMockStatus => ({
  ...defaultOsakiChargeBonusMockStatus(),
  state: (envValue as OsakiChargeBonusMockState) ?? "not-applied",
});

const osakiChargeBonusMockState = import.meta.env
  .VITE_MOCK_OSAKI_CHARGE_BONUS_STATUS as OsakiChargeBonusMockState;

const osakiChargeBonusMockStatuses: Record<
  OsakiChargeBonusCampaignTerm,
  OsakiChargeBonusMockStatus
> = {
  "1st": createOsakiChargeBonusMockStatus(osakiChargeBonusMockState),
  "2nd": createOsakiChargeBonusMockStatus(osakiChargeBonusMockState),
  "3rd": createOsakiChargeBonusMockStatus(osakiChargeBonusMockState),
};

const mockRepresentUserId = "019204ea-7c3a-7f9d-8f5c-7f5d3c6f0b8d";
let mockRepresentativeIndex = 0;
const mockRepresentations: PartialMessage<UserRepresentation>[] = [];

function toOsakiChargeBonusCampaignStatus(
  fundSource: PartialMessage<
    NonNullable<
      GetMyCampaignStatusesResponse_ChargeBonusCampaignStatus["fundSource"]
    >
  >,
  status: OsakiChargeBonusMockStatus,
): PartialMessage<GetMyCampaignStatusesResponse_ChargeBonusCampaignStatus> {
  return {
    fundSource,
    isApplied: status.state !== "not-applied",
    isWinner: status.state === "winner" || status.state === "consumed",
    maxQuantity:
      status.state === "not-applied" ? BigInt(0) : status.maxQuantity,
    consumedQuantity:
      status.state === "consumed"
        ? status.maxQuantity
        : status.consumedQuantity,
  };
}

function getCurrentOsakiChargeBonusMockTerm() {
  const mockServerDate = import.meta.env.VITE_MOCK_SERVER_DATE;
  const now = mockServerDate ? new Date(mockServerDate) : new Date();
  return getCurrentOsakiChargeBonusCampaignPeriod(now)?.term ?? "1st";
}

export function handleGetMyInvitation() {
  return new GetMyInvitationResponse({
    invitationCode: "2JSZtc",
    invitees: [
      {
        id: "019204ea-7c3a-7f9d-8f5c-7f5d3c6f0b8d",
        inviteeUserId: "019204eb-e359-7291-8643-28c5a37a2e2e",
      },
    ],
    isCampaignOver: false,
  });
}

export function handleCreateMyInviter(_: CreateMyInviterRequest) {
  return new CreateMyInviterResponse({});
}

export function handleApplyCampaign(_: ApplyCampaignRequest) {
  if (getTenantSlug() === "marumori") {
    return new ApplyCampaignResponse({
      campaignLottery: {
        userId: "019204eb-e359-7291-8643-28c5a37a2e2e",
        wonBalance: BigInt(2000),
        result: CampaignLottery_Result.WON,
        fundSourceId: "019204eb-e359-7291-8643-28c5a37a2e2e",
      },
    });
  }
  return new ApplyCampaignResponse({
    campaignLottery: {
      userId: "019204eb-e359-7291-8643-28c5a37a2e2e",
      wonBalance: BigInt(3000),
      result: CampaignLottery_Result.WON,
      fundSourceId: "019204eb-e359-7291-8643-28c5a37a2e2e",
    },
  });
}

export function handleGetMyCampaignStatuses(_: GetMyCampaignStatusesRequest) {
  return new GetMyCampaignStatusesResponse({
    r6Primary: {
      fundSource: {
        id: "019204eb-e359-7291-8643-28c5a37a2e2e",
        name: "R6一次新規登録キャンペーン",
        forbidTransfer: false,
      },
      isApplied: false,
      isWinLimitReached: true,
    },
    r6Additional: {
      fundSource: {
        id: "019204eb-f123-7291-8643-28c5a37a2e2e",
        name: "R6二次新規登録キャンペーン",
        forbidTransfer: false,
      },
      isApplied: false,
      isWinLimitReached: false,
    },
    r7OnSignup: {
      fundSource: {
        id: "019204eb-g456-7291-8643-28c5a37a2e2e",
        name: "R7新規登録キャンペーン",
        forbidTransfer: false,
      },
      isApplied: false,
      isWinLimitReached: false,
    },
    r8AntiInflation: {
      fundSource: {
        id: "019204eb-h789-7291-8643-28c5a37a2e2e",
        name: "R8物価高騰対策キャンペーン",
        forbidTransfer: false,
      },
      isApplied: false,
      isWinLimitReached: false,
    },
    r8MiyagiResidenceCard:
      getTenantSlug() === "miyagi"
        ? {
            fundSource: {
              id: "5d96e03a-a6b3-4a99-b6ca-5cb743458b9a",
              name: "ようこそみやぎ外国人ポイント",
              forbidTransfer: false,
            },
            isApplied: false,
            isWinLimitReached: false,
          }
        : undefined,
    r8OsakiChargeBonus1st:
      getTenantSlug() === "miyagi"
        ? toOsakiChargeBonusCampaignStatus(
            osakiChargeBonus1stFundSource,
            osakiChargeBonusMockStatuses["1st"],
          )
        : undefined,
    r8OsakiChargeBonus2nd:
      getTenantSlug() === "miyagi"
        ? toOsakiChargeBonusCampaignStatus(
            osakiChargeBonus2ndFundSource,
            osakiChargeBonusMockStatuses["2nd"],
          )
        : undefined,
    r8OsakiChargeBonus3rd:
      getTenantSlug() === "miyagi"
        ? toOsakiChargeBonusCampaignStatus(
            osakiChargeBonus3rdFundSource,
            osakiChargeBonusMockStatuses["3rd"],
          )
        : undefined,
  });
}

export function handleApplyChargeBonusCampaign(
  req: ApplyChargeBonusCampaignRequest,
) {
  const termByFundSourceId: Record<string, OsakiChargeBonusCampaignTerm> = {
    [osakiChargeBonus1stFundSource.id]: "1st",
    [osakiChargeBonus2ndFundSource.id]: "2nd",
    [osakiChargeBonus3rdFundSource.id]: "3rd",
  };
  const term = termByFundSourceId[req.fundSourceId] ?? "1st";
  const status = osakiChargeBonusMockStatuses[term];
  status.state = "applied";
  status.maxQuantity = req.maxQuantity;
  status.consumedQuantity = BigInt(0);
  return new ApplyChargeBonusCampaignResponse();
}

export function handleGetRepresentations() {
  return new GetRepresentationsResponse({
    represent: mockRepresentations,
    represented:
      import.meta.env.VITE_MOCK_KYOTO_REPRESENTED_BY_AGENT === "true"
        ? {
            representUserId: "019204ea-7c3a-7f9d-8f5c-000000000001",
            representedUserId: mockRepresentUserId,
            representedSubscriptionId: "mock-represented-subscription-id",
          }
        : undefined,
  });
}

export function handleApplySelfRepresentativeSignupCampaign() {
  if (
    !mockRepresentations.some(
      (representation) =>
        representation.representUserId === mockRepresentUserId &&
        representation.representedUserId === mockRepresentUserId,
    )
  ) {
    mockRepresentations.push({
      representUserId: mockRepresentUserId,
      representedUserId: mockRepresentUserId,
      representedSubscriptionId: "mock-self-subscription-id",
    });
  }
  return new ApplySelfRepresentativeSignupCampaignResponse();
}

export function handleApplyRepresentativeSignupCampaign(
  req: ApplyRepresentativeSignupCampaignRequest,
) {
  mockRepresentativeIndex += 1;
  mockRepresentations.push({
    representUserId: mockRepresentUserId,
    representedUserId: `019204ea-7c3a-7f9d-8f5c-${String(mockRepresentativeIndex).padStart(12, "0")}`,
    representedSubscriptionId: req.signedRepresentedSubscriptionIdJwt,
  });
  return new ApplyRepresentativeSignupCampaignResponse();
}

export function consumeOsakiChargeBonusQuantity(quantity: bigint) {
  const status =
    osakiChargeBonusMockStatuses[getCurrentOsakiChargeBonusMockTerm()];
  if (status.state !== "winner") {
    return;
  }
  status.consumedQuantity += quantity;
  if (status.consumedQuantity >= status.maxQuantity) {
    status.state = "consumed";
  }
}
